import type { LucideIcon } from "lucide-react";
import type { ReactNode } from "react";
import { Badge } from "@/components/ui/badge";
import { cn } from "@/lib/utils";

export const QUIZ_VISUALS = {
  certification: "/brand/moltjobs/proof-payout-flow.jpg",
  authoring: "/brand/moltjobs/escrow-command-core.jpg",
  operations: "/brand/moltjobs/agent-fleet-console.jpg",
};

type QuizVisualHeaderProps = {
  eyebrow: string;
  title: ReactNode;
  description?: ReactNode;
  imageSrc: string;
  icon?: LucideIcon;
  children?: ReactNode;
  meta?: ReactNode;
  className?: string;
};

export function QuizVisualHeader({
  eyebrow,
  title,
  description,
  imageSrc,
  icon: Icon,
  children,
  meta,
  className,
}: QuizVisualHeaderProps) {
  return (
    <section
      className={cn(
        "relative overflow-hidden rounded-2xl border border-divider/70 bg-card/80 px-5 py-5 shadow-lg shadow-black/10 sm:px-6 sm:py-6",
        className
      )}
    >
      <img
        src={imageSrc}
        alt=""
        aria-hidden="true"
        className="absolute inset-y-0 right-0 h-full w-full object-cover opacity-[0.18] md:w-[48%] md:opacity-[0.55]"
      />
      <div className="absolute inset-0 bg-gradient-to-r from-background via-background/92 to-background/55" />
      <div className="absolute inset-0 bg-gradient-to-br from-transparent via-transparent to-primary/10" />

      <div className="relative z-10 grid gap-5 md:grid-cols-[minmax(0,1fr)_minmax(180px,260px)] md:items-end">
        <div className="min-w-0">
          <div className="mb-3 flex flex-wrap items-center gap-2">
            <Badge className="border-primary/20 bg-primary/10 text-[10px] uppercase tracking-[0.22em] text-primary">
              {eyebrow}
            </Badge>
            {meta}
          </div>
          <h1 className="flex items-center gap-3 text-2xl font-bold font-display leading-tight sm:text-3xl">
            {Icon && <Icon className="h-7 w-7 shrink-0 text-primary" />}
            <span className="min-w-0">{title}</span>
          </h1>
          {description && (
            <div className="mt-2 max-w-2xl text-sm leading-6 text-muted-foreground sm:text-base">
              {description}
            </div>
          )}
        </div>

        <div className="flex flex-wrap items-center justify-start gap-2 md:justify-end">
          {children}
        </div>
      </div>
    </section>
  );
}
